import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

// Create context
const AppContext = createContext(null);

const normalizeAuthUser = (authUser) => {
  if (!authUser) return null;
  const profile = authUser.profile || {};
  const profileId = profile.id || profile._id || profile.userId || null;

  return {
    ...profile,
    id: profileId || authUser.id,
    userId: authUser.id,
    profileId,
    firstName: profile.first_name || profile.firstName || authUser.email?.split('@')[0] || 'User',
    lastName: profile.last_name || profile.lastName || '',
    email: authUser.email,
    phone: profile.phone || profile.primary_phone || profile.primaryPhone || authUser.phone || '',
    profilePhoto: profile.profilePhoto || profile.profile_photo || authUser.profilePhoto || authUser.profile_photo || null,
    profile_photo: profile.profile_photo || profile.profilePhoto || authUser.profile_photo || authUser.profilePhoto || null,
    photo: profile.photo || authUser.photo || '',
    role: authUser.role,
    registered: authUser.registered,
    kycStatus: authUser.kycStatus
  };
};

export const AppProvider = ({ children }) => {
  // Get auth state
  const { user: authUser, isAuthenticated, authApi } = useAuth();
  const role = authUser?.role || null;
  const currentUser = normalizeAuthUser(authUser);
  
  const [subscribers, setSubscribers] = useState([]);
  const [dependants, setDependants] = useState([]);
  const [careOfficers, setCareOfficers] = useState([]);
  const [visits, setVisits] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [careExpenses, setCareExpenses] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataError, setDataError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // New enhancement states
  const [ratings, setRatings] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [emergencies, setEmergencies] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [supplyRequests, setSupplyRequests] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [plans, setPlans] = useState([]);
  const [medicationLogs, setMedicationLogs] = useState([]);

  const applyBootstrapData = useCallback((data = {}) => {
    setSubscribers(data.subscribers || []);
    setDependants(data.dependants || []);
    setCareOfficers(data.careOfficers || []);
    setVisits(data.visits || []);
    setAlerts(data.alerts || []);
    setTransactions(data.transactions || []);
    setCareExpenses(data.careExpenses || []);
    setMetrics(data.metrics || null);
    setRatings(data.ratings || []);
    setAnomalies(data.anomalies || []);
    setEmergencies(data.emergencies || []);
    setConversations(data.conversations || []);
    setVendors(data.vendors || []);
    setSupplyRequests(data.supplyRequests || []);
    setPerformance(data.performance || []);
    setPlans(data.plans || []);
    setMedicationLogs(data.medicationLogs || []);
  }, []);

  const fetchLegacyData = useCallback(async () => {
    const requests = [
      ['subscribers', '/subscribers', setSubscribers, []],
      ['dependants', '/dependants', setDependants, []],
      ['careOfficers', '/care-officers', setCareOfficers, []],
      ['visits', '/visits', setVisits, []],
      ['alerts', '/alerts', setAlerts, []],
      ['transactions', '/transactions', setTransactions, []],
      ['careExpenses', '/care-expenses', setCareExpenses, []],
      ['metrics', '/admin/metrics', setMetrics, null],
      ['ratings', '/ratings', setRatings, []],
      ['anomalies', '/anomalies', setAnomalies, []],
      ['emergencies', '/emergencies', setEmergencies, []],
      ['conversations', '/conversations', setConversations, []],
      ['vendors', '/vendors', setVendors, []],
      ['supplyRequests', '/supply-requests', setSupplyRequests, []],
      ['performance', '/performance', setPerformance, []],
      ['plans', '/plans', setPlans, []]
    ];

    const results = await Promise.allSettled(
      requests.map(([key, path]) => authApi.get(path).then(response => ({ key, data: response.data })))
    );

    const criticalFailures = [];
    results.forEach((result, index) => {
      const [key, path, setter, fallback] = requests[index];
      if (result.status === 'fulfilled') {
        setter(result.value.data);
        return;
      }

      setter(fallback);
      const status = result.reason?.response?.status;
      if (!['metrics', 'performance'].includes(key) && ![401, 403, 404].includes(status)) {
        criticalFailures.push({ key, path, error: result.reason });
      }
    });

    return criticalFailures;
  }, [authApi]);

  // Fetch role-scoped app data
  const fetchData = useCallback(async (options = {}) => {
    const background = options.background === true;
    if (!isAuthenticated || authUser?.registered === false) {
      if (!background) setLoading(false);
      return;
    }

    if (!background) setLoading(true);
    setDataError(null);

    try {
      const response = await authApi.get('/bootstrap', {
        params: options.force ? { force: true } : undefined
      });
      applyBootstrapData(response.data || {});
    } catch (bootstrapError) {
      console.warn('Bootstrap failed; falling back to legacy data loading:', bootstrapError);
      const criticalFailures = await fetchLegacyData();
      if (criticalFailures.length > 0) {
        console.error('Error fetching Magnolia data:', criticalFailures);
        setDataError('Some Magnolia data could not be loaded. Please refresh if anything looks stale.');
      }
    } finally {
      if (!background) setLoading(false);
    }
  }, [applyBootstrapData, authApi, authUser?.registered, fetchLegacyData, isAuthenticated]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get dependants for current subscriber
  const getSubscriberDependants = (subscriberId) => {
    if (role === 'subscriber') {
      const possibleIds = new Set([
        subscriberId,
        currentUser?.profileId,
        currentUser?.id,
        currentUser?.userId,
        currentUser?._id
      ].filter(Boolean));
      const matched = dependants.filter(d => possibleIds.has(d.subscriberId) || possibleIds.has(d.subscriber_id));
      return matched.length > 0 ? matched : dependants;
    }
    return dependants.filter(d => d.subscriberId === subscriberId || d.subscriber_id === subscriberId);
  };

  // Get visits for subscriber
  const getSubscriberVisits = (subscriberId) => {
    if (role === 'subscriber') {
      const possibleIds = new Set([
        subscriberId,
        currentUser?.profileId,
        currentUser?.id,
        currentUser?.userId,
        currentUser?._id
      ].filter(Boolean));
      const matched = visits.filter(v => possibleIds.has(v.subscriberId) || possibleIds.has(v.subscriber_id));
      return matched.length > 0 ? matched : visits;
    }
    return visits.filter(v => v.subscriberId === subscriberId || v.subscriber_id === subscriberId);
  };

  // Get visits for care officer
  const getCareOfficerVisits = (careOfficerId) => {
    return visits.filter(v => v.careOfficerId === careOfficerId || v.care_officer_id === careOfficerId);
  };

  const upsertVisit = useCallback((visit) => {
    if (!visit?.id) return;
    setVisits(prev => {
      const exists = prev.some(item => item.id === visit.id);
      if (exists) {
        return prev.map(item => item.id === visit.id ? visit : item);
      }
      return [visit, ...prev];
    });
  }, []);

  const upsertDependant = useCallback((dependant) => {
    if (!dependant?.id) return;
    setDependants(prev => {
      const exists = prev.some(item => item.id === dependant.id);
      if (exists) {
        return prev.map(item => item.id === dependant.id ? dependant : item);
      }
      return [dependant, ...prev];
    });
  }, []);

  const upsertConversation = useCallback((conversation) => {
    if (!conversation?.id) return;
    setConversations(prev => {
      const exists = prev.some(item => item.id === conversation.id);
      return exists
        ? prev.map(item => item.id === conversation.id ? { ...item, ...conversation } : item)
        : [conversation, ...prev];
    });
  }, []);

  const startCareOfficerConversation = async (careOfficerId, visitId = null) => {
    const response = await authApi.post('/conversations/care-officer', {
      care_officer_id: careOfficerId,
      visit_id: visitId,
    });
    upsertConversation(response.data);
    await fetchData({ force: true });
    return response.data;
  };

  const startFamilyConversation = async () => {
    const response = await authApi.post('/conversations/family', {});
    upsertConversation(response.data);
    await fetchData({ force: true });
    return response.data;
  };

  const createSupplyRequest = async ({ category, description, urgent = false }) => {
    const response = await authApi.post('/supply-requests', { category, description, urgent });
    setSupplyRequests(prev => [response.data, ...prev]);
    await fetchData({ force: true });
    return response.data;
  };

  const triggerEmergency = async (location = {}) => {
    const response = await authApi.post('/emergencies', location);
    setEmergencies(prev => [response.data, ...prev]);
    await fetchData({ force: true, background: true });
    return response.data;
  };

  const refreshEmergency = async (emergencyId) => {
    const response = await authApi.get(`/emergencies/${emergencyId}`);
    setEmergencies(prev => prev.map((item) => (
      (item.id || item._id) === emergencyId ? response.data : item
    )));
    return response.data;
  };

  const sendEmergencyLocationUpdate = async (emergencyId, location) => {
    return authApi.post(`/emergencies/${emergencyId}/location`, location);
  };

  const submitEmergencyFeedback = async (emergencyId, rating, comment = '') => {
    const response = await authApi.post(`/emergencies/${emergencyId}/malaika-feedback`, {
      rating,
      comment: comment || undefined,
    });
    // Refresh the emergency so the local state reflects feedback_submitted
    await refreshEmergency(emergencyId);
    return response.data;
  };

  const markMedicationTaken = async ({ medicationName, scheduledTime = '' }) => {
    const response = await authApi.post('/dependants/me/medication-logs', {
      medication_name: medicationName,
      scheduled_time: scheduledTime,
      status: 'Taken',
    });
    setMedicationLogs(prev => {
      const exists = prev.some(item => item.id === response.data.id);
      return exists ? prev.map(item => item.id === response.data.id ? response.data : item) : [response.data, ...prev];
    });
    return response.data;
  };

  const sendConversationMessage = async (conversationId, text) => {
    const response = await authApi.post(`/conversations/${conversationId}/messages`, { text });
    const message = response.data;
    setConversations(prev => prev.map(conversation => {
      if (conversation.id !== conversationId) return conversation;
      const currentMessages = conversation.messages || [];
      return {
        ...conversation,
        messages: [...currentMessages, message],
        lastMessage: {
          senderId: message.senderId,
          senderName: message.senderName,
          text: message.text,
          timestamp: message.timestamp,
          read: false,
        },
      };
    }));
    return message;
  };

  const reviewVisit = async (visitId, status, comments = '') => {
    const response = await authApi.patch(`/visits/${visitId}/review`, { status, comments });
    const updated = response.data;
    setVisits(prev => prev.map(visit => visit.id === updated.id ? updated : visit));
    return updated;
  };

  const submitVisitRating = async (visitId, score, feedback = '') => {
    const response = await authApi.post(`/visits/${visitId}/rating`, { score, feedback });
    const updated = response.data;
    setRatings(prev => {
      const exists = prev.some(item => item.id === updated.id);
      return exists
        ? prev.map(item => item.id === updated.id ? updated : item)
        : [updated, ...prev];
    });
    await fetchData({ force: true });
    return updated;
  };

  // Get alerts for subscriber
  const getSubscriberAlerts = (subscriberId) => {
    if (role === 'subscriber') {
      const possibleIds = new Set([
        subscriberId,
        currentUser?.profileId,
        currentUser?.id,
        currentUser?.userId,
        currentUser?._id
      ].filter(Boolean));
      const matched = alerts.filter(a => possibleIds.has(a.subscriberId) || possibleIds.has(a.subscriber_id));
      return matched.length > 0 ? matched : alerts;
    }
    return alerts.filter(a => a.subscriberId === subscriberId);
  };

  // Get transactions for subscriber
  const getSubscriberTransactions = (subscriberId) => {
    return transactions.filter(t => t.subscriberId === subscriberId);
  };

  // Get pending reviews (for supervisor)
  const getPendingReviews = () => {
    return visits.filter(v => v.status === 'Submitted');
  };

  // Get care officer by ID
  const getCareOfficerById = (id) => {
    return careOfficers.find(co => co.id === id || co._id === id);
  };

  // Get dependant by ID
  const getDependantById = (id) => {
    return dependants.find(d => d.id === id);
  };

  // Get subscriber by ID
  const getSubscriberById = (id) => {
    return subscribers.find(s => s.id === id);
  };

  // Acknowledge alert
  const acknowledgeAlert = async (alertId) => {
    try {
      await authApi.patch(`/alerts/${alertId}/acknowledge`);
      setAlerts(prev => prev.map(a => 
        a.id === alertId ? { ...a, status: 'Acknowledged', acknowledgedAt: new Date().toISOString() } : a
      ));
    } catch (error) {
      console.error('Error acknowledging alert:', error);
    }
  };

  // Approve transaction
  const approveTransaction = async (transactionId) => {
    try {
      await authApi.patch(`/transactions/${transactionId}/approve`);
      setTransactions(prev => prev.map(t => 
        t.id === transactionId ? { ...t, status: 'Approved', completedAt: new Date().toISOString() } : t
      ));
    } catch (error) {
      console.error('Error approving transaction:', error);
    }
  };

  // Get unread alerts count
  const getUnreadAlertsCount = (subscriberId) => {
    if (role === 'supervisor' || role === 'admin') {
      return alerts.filter(a => {
        const audienceRoles = a.audienceRoles || a.audience_roles || [];
        return a.status === 'New' && (
          a.audience === 'supervisor' ||
          audienceRoles.includes(role) ||
          audienceRoles.includes('supervisor')
        );
      }).length;
    }
    return alerts.filter(a => a.subscriberId === subscriberId && a.status === 'New').length;
  };

  // Get pending transactions
  const getPendingTransactions = (subscriberId) => {
    return transactions.filter(t => t.subscriberId === subscriberId && t.status === 'Pending Approval');
  };

  const getSubscriberCareExpenses = (subscriberId) => {
    if (role === 'subscriber') {
      const possibleIds = new Set([
        subscriberId,
        currentUser?.profileId,
        currentUser?.id,
        currentUser?.userId,
        currentUser?._id
      ].filter(Boolean));
      const matched = careExpenses.filter(item => possibleIds.has(item.subscriberId) || possibleIds.has(item.subscriber_id));
      return matched.length > 0 ? matched : careExpenses;
    }
    return careExpenses.filter(item => item.subscriberId === subscriberId || item.subscriber_id === subscriberId);
  };

  const getPendingCareExpenses = (subscriberId) => (
    getSubscriberCareExpenses(subscriberId).filter(item => item.status === 'Pending Subscriber Approval')
  );

  const approveCareExpense = async (expenseId) => {
    const response = await authApi.patch(`/care-expenses/${expenseId}/approve`, {});
    const updated = response.data;
    setCareExpenses(prev => prev.map(item => item.id === updated.id ? updated : item));
    return updated;
  };

  const declineCareExpense = async (expenseId, reason = '') => {
    const response = await authApi.patch(`/care-expenses/${expenseId}/decline`, { reason });
    const updated = response.data;
    setCareExpenses(prev => prev.map(item => item.id === updated.id ? updated : item));
    return updated;
  };

  // New enhancement helpers
  const getVisitRating = (visitId) => {
    return ratings.find(r => r.visitId === visitId || r.visit_id === visitId);
  };

  const getCareOfficerRatings = (careOfficerId) => {
    return ratings.filter(r => r.careOfficerId === careOfficerId || r.care_officer_id === careOfficerId);
  };

  const getCareOfficerAverageRating = (careOfficerId) => {
    const officerRatings = getCareOfficerRatings(careOfficerId);
    if (officerRatings.length === 0) return 0;
    return officerRatings.reduce((sum, r) => sum + r.subscriberRating.score, 0) / officerRatings.length;
  };

  const getSubscriberEmergencies = (subscriberId) => {
    return emergencies.filter(e => e.subscriberId === subscriberId);
  };

  const getActiveEmergencies = () => {
    return emergencies.filter(e => e.status !== 'Resolved' && e.status !== 'False Alarm');
  };

  const getUserConversations = (userId) => {
    return conversations.filter(c => c.participants.includes(userId));
  };

  const getConversationById = (conversationId) => {
    return conversations.find(c => c.id === conversationId || c._id === conversationId);
  };

  const getUnreadMessagesCount = (userId) => {
    return conversations.reduce((count, conv) => {
      return count + (conv.unreadCount?.[userId] || 0);
    }, 0);
  };

  const getVendorsByCategory = (category) => {
    return vendors.filter(v => v.category === category);
  };

  const getVendorsByState = (state) => {
    return vendors.filter(v => v.state === state || v.state === 'National');
  };

  const getSubscriberSupplyRequests = (subscriberId) => {
    return supplyRequests.filter(r => r.subscriberId === subscriberId);
  };

  const getPendingSupplyRequests = (subscriberId) => {
    return supplyRequests.filter(r => r.subscriberId === subscriberId && r.status === 'Requested');
  };

  const getCareOfficerPerformance = (careOfficerId) => {
    return performance.find(p => p.officerId === careOfficerId);
  };

  const getOfficersByTier = (tier) => {
    return performance.filter(p => p.tier === tier);
  };

  const getAnomaliesByOfficer = (careOfficerId) => {
    return anomalies.filter(a => a.careOfficerId === careOfficerId);
  };

  const getPendingAnomalies = () => {
    return anomalies.filter(a => (a.anomalyFlags || []).some(f => f.status === 'Under Review'));
  };

  const value = {
    role,
    currentUser,
    subscribers,
    dependants,
    careOfficers,
    visits,
    alerts,
    transactions,
    careExpenses,
    metrics,
    loading,
    dataError,
    sidebarOpen,
    setSidebarOpen,
    fetchData,
    getSubscriberDependants,
    getSubscriberVisits,
    getCareOfficerVisits,
    upsertVisit,
    upsertDependant,
    upsertConversation,
    reviewVisit,
    submitVisitRating,
    startCareOfficerConversation,
    startFamilyConversation,
    createSupplyRequest,
    triggerEmergency,
    refreshEmergency,
    sendEmergencyLocationUpdate,
    submitEmergencyFeedback,
    markMedicationTaken,
    sendConversationMessage,
    getSubscriberAlerts,
    getSubscriberTransactions,
    getPendingReviews,
    getCareOfficerById,
    getDependantById,
    getSubscriberById,
    acknowledgeAlert,
    approveTransaction,
    getUnreadAlertsCount,
    getPendingTransactions,
    getSubscriberCareExpenses,
    getPendingCareExpenses,
    approveCareExpense,
    declineCareExpense,
    // New enhancement exports
    ratings,
    anomalies,
    emergencies,
    conversations,
    vendors,
    supplyRequests,
    performance,
    plans,
    medicationLogs,
    getVisitRating,
    getCareOfficerRatings,
    getCareOfficerAverageRating,
    getSubscriberEmergencies,
    getActiveEmergencies,
    getUserConversations,
    getConversationById,
    getUnreadMessagesCount,
    getVendorsByCategory,
    getVendorsByState,
    getSubscriberSupplyRequests,
    getPendingSupplyRequests,
    getCareOfficerPerformance,
    getOfficersByTier,
    getAnomaliesByOfficer,
    getPendingAnomalies,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;
